import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";

import { useEffect, useState } from "react";
import { CalendarDaysIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "@/components/ScrollToTop";
import ShowLoader from "@/components/Loader";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    fetchCountry,
    fetchCity,
    fetchOffice,
} from "@/store/slices/internalUsersSlice";
import { Input, Typography } from "@material-tailwind/react";
import { assignRfqToSalesManager, fetchSalesManager, getRfqCommission, getSalesMemberById, resetStatus, updateRfqCommission } from "@/store/slices/rfqSlice";
import Toaster from "@/components/Toaster";
import { getSettingData } from "@/store/slices/settingSlice";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '8px',
        minHeight: '44px',
        borderColor: state.isFocused ? '#212121' : '#b0bec5',
        boxShadow: state.isFocused ? '0 0 0 1px #212121' : 'none',
        '&:hover': {
            borderColor: state.isFocused ? '#212121' : '#b0bec5',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#9e9e9e',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
        borderRadius: '8px',
    })
};
const priorities = [
    {
        label: "High",
        value: "high"
    },
    {
        label: "Medium",
        value: "medium"
    },
    {
        label: "Low",
        value: "low"
    }
];
const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const schema = yup.object().shape({
    deadline: yup.string().required("deadline_required"),
    priority: yup.string().required("priority_required"),
    commission_type: yup.string().required("commission_type_required"),
    notes: yup.string()
        .required("note_required")
        .matches(notBlankRegex, "note_empty_error")
        .matches(noHTMLRegex, "HTML tags are not allowed")
        .matches(noCodeRegex, "Code content is not allowed")
        .matches(noConsecutiveSpecialChars, "Note cannot contain consecutive special characters")
        .matches(noLeadingTrailingPunct, "Note cannot start or end with punctuation")
        .min(2, "note_min_length_error")
        .max(100, "note_max_length_error"),
    commission_sales_manager: yup
        .number()
        .typeError("commission_value_required#{\"role\":\"Sales Manager\"}")
        .min(0, "commission_min_value_error")
        .when("commission_type", {
            is: "percentage",
            then: (s) => s.max(100, "commission_percentage_max_error"),
            otherwise: (s) => s.max(99999, "commission_flat_max_error"),
        }),

    // commission_sales_rep: yup
    //     .number()
    //     .typeError("commission_value_required#{\"role\":\"Sales Rep\"}")
    //     .min(0, "commission_min_value_error")
    //     .when("commission_type", {
    //         is: "percentage",
    //         then: (s) => s.max(100, "commission_percentage_max_error"),
    //         otherwise: (s) => s.max(99999, "commission_flat_max_error"),
    //     }),

    commission_procurement_manager: yup
        .number()
        .typeError("commission_value_required#{\"role\":\"Procurement Manager\"}")
        .min(0, "commission_min_value_error")
        .when("commission_type", {
            is: "percentage",
            then: (s) => s.max(100, "commission_percentage_max_error"),
            otherwise: (s) => s.max(99999, "commission_flat_max_error"),
        }),

    // commission_procurement_rep: yup
    //     .number()
    //     .typeError("commission_value_required#{\"role\":\"Procurement Rep\"}")
    //     .min(0, "commission_min_value_error")
    //     .when("commission_type", {
    //         is: "percentage",
    //         then: (s) => s.max(100, "commission_percentage_max_error"),
    //         otherwise: (s) => s.max(99999, "commission_flat_max_error"),
    //     }),

    time_slot_from_sales_manager: yup
        .string()
        .required("Sales Manager time slot is required")
        .test("from>=now", "Sales Manager slot 'From' cannot be in the past", function (v) {
            if (!v) return true;
            return new Date(v).getTime() >= new Date().getTime();
        })
        .test("from<=deadline", "Sales Manager slot 'From' cannot exceed the deadline", function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        }),
    time_slot_to_sales_manager: yup
        .string()
        .required("Sales Manager time slot is required")
        .test("to<=deadline", "Sales Manager slot 'To' cannot exceed the deadline", function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        })
        .test("to>=from", "Sales Manager slot 'To' cannot be before 'From'", function (v) {
            const from = this.parent?.time_slot_from_sales_manager;
            if (!v || !from) return true;
            return new Date(v).getTime() >= new Date(from).getTime();
        }),

    // time_slot_from_sales_rep: yup
    //     .string()
    //     .required("Sales Representative time slot is required")
    //     .test("from>=now", "Sales Representative slot 'From' cannot be in the past", function (v) {
    //         if (!v) return true;
    //         return new Date(v).getTime() >= new Date().getTime();
    //     })
    //     .test("from<=deadline", "Sales Representative slot 'From' cannot exceed the deadline", function (v) {
    //         const deadline = this.parent?.deadline;
    //         if (!v || !deadline) return true;
    //         return new Date(v).getTime() <= new Date(deadline).getTime();
    //     }),
    // time_slot_to_sales_rep: yup
    //     .string()
    //     .required("Sales Representative time slot is required")
    //     .test("to<=deadline", "Sales Representative slot 'To' cannot exceed the deadline", function (v) {
    //         const deadline = this.parent?.deadline;
    //         if (!v || !deadline) return true;
    //         return new Date(v).getTime() <= new Date(deadline).getTime();
    //     })
    //     .test("to>=from", "Sales Representative slot 'To' cannot be before 'From'", function (v) {
    //         const from = this.parent?.time_slot_from_sales_rep;
    //         if (!v || !from) return true;
    //         return new Date(v).getTime() >= new Date(from).getTime();
    //     }),

    time_slot_from_procurement_manager: yup
        .string()
        .required("timeslot_required#{\"role\":\"Procurement Manager\"}")
        .test("from>=now", "timeslot_past_error#{\"role\":\"Procurement Manager\"}", function (v) {
            if (!v) return true;
            return new Date(v).getTime() >= new Date().getTime();
        })
        .test("from<=deadline", "timeslot_exceed_deadline_error#{\"role\":\"Procurement Manager\",\"direction\":\"From\"}", function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        }),
    time_slot_to_procurement_manager: yup
        .string()
        .required("timeslot_required#{\"role\":\"Procurement Manager\"}")
        .test("to<=deadline", "timeslot_exceed_deadline_error#{\"role\":\"Procurement Manager\",\"direction\":\"To\"}", function (v) {
            const deadline = this.parent?.deadline;
            if (!v || !deadline) return true;
            return new Date(v).getTime() <= new Date(deadline).getTime();
        })
        .test("to>=from", "timeslot_to_before_from_error#{\"role\":\"Procurement Manager\"}", function (v) {
            const from = this.parent?.time_slot_from_procurement_manager;
            if (!v || !from) return true;
            return new Date(v).getTime() >= new Date(from).getTime();
        }),

    // time_slot_from_procurement_rep: yup
    //     .string()
    //     .required("Procurement Team Member time slot is required")
    //     .test("from>=now", "Procurement Team Member slot 'From' cannot be in the past", function (v) {
    //         if (!v) return true;
    //         return new Date(v).getTime() >= new Date().getTime();
    //     })
    //     .test("from<=deadline", "Procurement Team Member slot 'From' cannot exceed the deadline", function (v) {
    //         const deadline = this.parent?.deadline;
    //         if (!v || !deadline) return true;
    //         return new Date(v).getTime() <= new Date(deadline).getTime();
    //     }),
    // time_slot_to_procurement_rep: yup
    //     .string()
    //     .required("Procurement Team Member time slot is required")
    //     .test("to<=deadline", "Procurement Team Member slot 'To' cannot exceed the deadline", function (v) {
    //         const deadline = this.parent?.deadline;
    //         if (!v || !deadline) return true;
    //         return new Date(v).getTime() <= new Date(deadline).getTime();
    //     })
    //     .test("to>=from", "Procurement Team Member slot 'To' cannot be before 'From'", function (v) {
    //         const from = this.parent?.time_slot_from_procurement_rep;
    //         if (!v || !from) return true;
    //         return new Date(v).getTime() >= new Date(from).getTime();
    //     }),
    deduction_reason: yup.string().when("is_commission_changed", {
        is: true,
        then: (schema) =>
            schema
                .required("deduction_reason_required")
                .matches(notBlankRegex, "field_not_empty")
                .matches(noHTMLRegex, "HTML tags are not allowed")
                .matches(noCodeRegex, "Code content is not allowed")
                .min(5, "deduction_reason_min")
                .max(255, "deduction_reason_max"),
        otherwise: (schema) => schema.nullable(),
    }),

});

export function EditRFQCommissionDeadline({ isOpen, onClose, rfqId, assigned_details = null }) {
    const dispatch = useDispatch();
    const { settings } = useSelector((state) => state.setting);
    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            rfq_ids: [rfqId],
            commission_type: "percentage",
            notes: null,
            deduction_reason: "",
            is_commission_changed: false,
            time_slot_from_sales_manager: "",
            time_slot_to_sales_manager: "",
            time_slot_from_sales_rep: "",
            time_slot_to_sales_rep: "",
            time_slot_from_procurement_manager: "",
            time_slot_to_procurement_manager: "",
            time_slot_from_procurement_rep: "",
            time_slot_to_procurement_rep: "",
        }, resolver: yupResolver(schema)
    });

    const { cities, countries, offices } = useSelector((state) => state.internalUser);
    // Destructure with a default empty array to be safe
    const { rfq_commission, loading } = useSelector((state) => state.rfq);
    const { user } = useSelector((state) => state.user); // Added/Ensured hook access to the active user profile
    const currentUserRole = user?.roles?.[0] || "";
    const isAuthorizedToUpdate = currentUserRole === "Administrator" || currentUserRole === "Sub Admin";

    useEffect(() => {
        dispatch(getRfqCommission({ rfq_id: rfqId }));
        dispatch(getSettingData());
    }, [dispatch]);

    const commissionType = watch("commission_type");

    const currentCommissionValues = watch([
        "commission_sales_manager",
        "commission_sales_rep",
        "commission_procurement_manager",
        "commission_procurement_rep",
    ]);

    const isCommissionChanged =
        rfq_commission &&
        (
            Number(currentCommissionValues[0]) !== Number(rfq_commission.commission_sales_manager) ||
            Number(currentCommissionValues[1]) !== Number(rfq_commission.commission_sales_rep) ||
            Number(currentCommissionValues[2]) !== Number(rfq_commission.commission_procurement_manager) ||
            Number(currentCommissionValues[3]) !== Number(rfq_commission.commission_procurement_rep)
        );

    const allCommissions = [
        { text: t("commission_sales_member_label"), value: 0, key: "commission_sales_rep", fromKey: "time_slot_from_sales_rep", toKey: "time_slot_to_sales_rep" },
        { text: t("commission_sales_manager_label"), value: 0, key: "commission_sales_manager", fromKey: "time_slot_from_sales_manager", toKey: "time_slot_to_sales_manager" },
        { text: t("commission_procurement_manager_label"), value: 0, key: "commission_procurement_manager", fromKey: "time_slot_from_procurement_manager", toKey: "time_slot_to_procurement_manager" },
        { text: t("commission_procurement_member_label"), value: 0, key: "commission_procurement_rep", fromKey: "time_slot_from_procurement_rep", toKey: "time_slot_to_procurement_rep" },
    ];

    const getVisibleCommissions = () => {
        const isSales = currentUserRole.startsWith("Sales");
        const isProcurement = currentUserRole.startsWith("Procurement");
        const isMember = user?.designation === "member";

        if (isSales) {
            let commissions = allCommissions.filter(c => c.key.includes("sales"));
            if (isMember) {
                commissions = commissions.filter(c => !c.key.includes("manager"));
            }
            return commissions;
        }
        if (isProcurement) {
            let commissions = allCommissions.filter(c => c.key.includes("procurement"));
            if (isMember) {
                commissions = commissions.filter(c => !c.key.includes("manager"));
            }
            return commissions;
        }
        return allCommissions;
    };

    const defaultCommissions = getVisibleCommissions();

    const onSubmit = async (data) => {
        try {
            const payload = {
                ...data,
                sales_members: [selectedUsers],
            };
            const result = await dispatch(updateRfqCommission(payload)).unwrap();
            if (result?.status) {
                Toaster.success(result.message || t("commission_update_success"));
                dispatch(resetStatus());
                reset();
                onClose();
            } else {
                Toaster.error(result.message || ct("somethingWentWrong"));
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || t("commission_update_failed"));
            dispatch(resetStatus());
        }
    };

    useEffect(() => {
        if (!rfq_commission) return;

        const formatDateTime = (value) => {
            if (!value) return "";
            return value.slice(0, 16); // ✅ correct for datetime-local
        };

        const formattedData = {
            ...rfq_commission,


            time_slot_from_sales_manager: formatDateTime(rfq_commission.sales_manager_time_slot_from),
            time_slot_to_sales_manager: formatDateTime(rfq_commission.sales_manager_time_slot_to),

            time_slot_from_sales_rep: formatDateTime(rfq_commission.sales_rep_time_slot_from),
            time_slot_to_sales_rep: formatDateTime(rfq_commission.sales_rep_time_slot_to),

            time_slot_from_procurement_manager: formatDateTime(rfq_commission.procurement_manager_time_slot_from),
            time_slot_to_procurement_manager: formatDateTime(rfq_commission.procurement_manager_time_slot_to),

            time_slot_from_procurement_rep: formatDateTime(rfq_commission.procurement_rep_time_slot_from),
            time_slot_to_procurement_rep: formatDateTime(rfq_commission.procurement_rep_time_slot_to),

            // also fix deadline
            deadline: formatDateTime(rfq_commission.deadline),
        };

        reset(formattedData);

    }, [rfq_commission]);

    const now = new Date();
    const minDateTime = now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
    const deadlineValue = watch("deadline");


    const [selectedUsers, setSelectedUsers] = useState(null);

    useEffect(() => {
        const deadline = watch("deadline");
        const subscription = watch((value, { name, type }) => {
            if (name === 'deadline' && type === 'change') {
                setValue("time_slot_from_sales_rep", "");
                setValue("time_slot_to_sales_rep", "");
                setValue("time_slot_from_sales_manager", "");
                setValue("time_slot_to_sales_manager", "");
                setValue("time_slot_from_procurement_manager", "");
                setValue("time_slot_to_procurement_manager", "");
                setValue("time_slot_from_procurement_rep", "");
                setValue("time_slot_to_procurement_rep", "");
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setValue]);

    useEffect(() => {
        let value = watch('time_slot_to_sales_rep');
        setValue('time_slot_from_sales_manager', value);
    }, [watch('time_slot_to_sales_rep')]);

    useEffect(() => {
        let value = watch('time_slot_to_sales_manager');
        setValue('time_slot_from_procurement_manager', value);
    }, [watch('time_slot_to_sales_manager')]);

    useEffect(() => {
        let value = watch('time_slot_to_procurement_manager');
        setValue('time_slot_from_procurement_rep', value);
    }, [watch('time_slot_to_procurement_manager')]);

    const isSalesMemberAvailable =
        assigned_details?.some(
            (item) =>
                item.type === "Sales Representative" &&
                item.designation === "Member"
        ) ?? false;

    const isProcurementMemberAvailable =
        assigned_details?.some(
            (item) =>
                item.type === "Procurement" &&
                item.designation === "Member"
        ) ?? false;

    return (
        <Dialog
            open={isOpen}
            handler={onClose}
            size="md"
            className="rounded-2xl p-0"
        >
            {loading && <ShowLoader />}
            <button type="button" className="sr-only" aria-hidden="true" tabIndex={0} />
            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {t("edit_commission_deadline_title")}
                </h2>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)}>
                <button type="button" className="sr-only" aria-hidden="true" tabIndex={0} />
                <div className="px-6 max-h-[65vh] overflow-y-auto mt-5">
                    <DialogBody>
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-6 mb-6">

                            {/* <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    Country <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder="Select Country"
                                    options={countries}
                                    onChange={(c) => {
                                        setValue("country_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchCity(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.country_id && (
                                    <p className="text-red-500 text-sm">{errors.country_id.message}</p>
                                )}
                            </div>

                           
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    City <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder="Select City"
                                    options={cities}
                                    onChange={(c) => {
                                        setValue("city_id", c?.value, { shouldValidate: true });
                                        dispatch(fetchOffice(c?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.city_id && (
                                    <p className="text-red-500 text-sm">{errors.city_id.message}</p>
                                )}
                            </div>

                            
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    Office <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder="Select Office"
                                    options={offices}
                                    onChange={(o) => {
                                        setValue("office_id", o?.value, { shouldValidate: true });
                                        if (o?.value) dispatch(fetchSalesManager(o?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.office_id && (
                                    <p className="text-red-500 text-sm">{errors.office_id.message}</p>
                                )}
                            </div>

                            
                            <div>
                                <Typography variant="small" className="mb-2 font-medium">
                                    Sales Manager <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder="Select Sales Manager"
                                    options={sales_managers}
                                    onChange={(m) => {
                                        setValue("sales_representative_id", m?.value, {
                                            shouldValidate: true,
                                        });
                                        setSelectedUsers(null);
                                        dispatch(getSalesMemberById(m?.value));
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                />
                                {errors.sales_representative_id && (
                                    <p className="text-red-500 text-sm">
                                        {errors.sales_representative_id.message}
                                    </p>
                                )}
                            </div>
                            {sales_team && <div className="col-span-2">
                                <Typography variant="small" className="mb-2 font-medium">
                                    Sales Team Members 
                                </Typography>
                                <Select
                                    placeholder="Select Sales Member"
                                    options={sales_team}
                                    onChange={(m) => {
                                        // Store just the ID or null
                                        setSelectedUsers(m ? m.value : null); 
                                    }}
                                    styles={customSelectStyles}
                                    isClearable
                                    noOptionsMessage={() => "No Sales Member Available"}
                                    // This is the critical fix:
                                    value={sales_team?.find(option => option.value === selectedUsers) || null}
                                />
                            </div>} */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("rfq_deadline_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Input
                                    type="datetime-local"
                                    min={minDateTime}
                                    disabled={!isAuthorizedToUpdate}
                                    {...register("deadline")}
                                    className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                    labelProps={{ className: "before:content-none after:content-none" }}
                                />
                                {errors.deadline && (
                                    <p className="text-red-500 text-sm">{t(errors.deadline.message)}</p>
                                )}
                            </div>

                            {/* Priority */}
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("rfq_priority_label")} <span className="text-red-500">*</span>
                                </Typography>
                                <Select
                                    placeholder={t("select_priority_placeholder")}
                                    options={priorities}
                                    value={priorities.find(p => p.value === watch("priority")) || null}
                                    onChange={(priority) =>
                                        setValue("priority", priority?.value, { shouldValidate: true })
                                    }
                                    styles={customSelectStyles}
                                    isClearable
                                    isDisabled={!isAuthorizedToUpdate}
                                />
                                {errors.priority && (
                                    <p className="text-red-500 text-sm">{t(errors.priority.message)}</p>
                                )}
                            </div>
                        </div>

                        {/* Commission Section */}
                        <div className="grid grid-cols-1 gap-6 mb-6">
                            <div>
                                <Typography variant="small" className="mb-2 font-medium capitalize">
                                    {t("set_commission_label")}
                                </Typography>
                                <div className="flex items-center gap-6 mb-3">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" value="percentage" {...register("commission_type")} disabled={!isAuthorizedToUpdate} />
                                        {t("percentage_commission_label")}
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" value="flat" {...register("commission_type")} disabled={!isAuthorizedToUpdate} />
                                        {t("flat_commission_label")}
                                    </label>
                                </div>

                                <div className="space-y-4">
                                    {defaultCommissions.map((item, index) => (
                                        <div key={index} className="flex flex-col gap-1">
                                            <div className="bg-gray-50 rounded-md">

                                                <div className="flex items-center justify-between px-4 py-2">
                                                    <p className="text-sm text-gray-800">{item.text}</p>
                                                    <div className="flex items-center gap-3 flex-wrap justify-end">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                step="any"
                                                                type="number"
                                                                disabled={!isAuthorizedToUpdate}
                                                                {...register(item.key)}
                                                                className={`w-20 text-center border rounded-md py-1 text-sm ${errors[item.key] ? "border-red-500" : "border-gray-300"}`}
                                                            />
                                                            <span className="text-gray-600 text-sm">
                                                                {watch("commission_type") === "percentage" ? "%" : "SAR"}
                                                            </span>
                                                        </div>

                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between px-4 py-2">
                                                    <span className="text-gray-600 text-xs whitespace-nowrap capitalize">
                                                        {t("timeslot_label_simple")}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="datetime-local"
                                                            disabled={
                                                                !isAuthorizedToUpdate ||
                                                                !deadlineValue ||
                                                                (!isAuthorizedToUpdate && item.key === "commission_sales_rep" && !isSalesMemberAvailable) ||
                                                                (!isAuthorizedToUpdate && item.key === "commission_procurement_rep" && !isProcurementMemberAvailable)
                                                            }
                                                            min={minDateTime}
                                                            max={deadlineValue || undefined}
                                                            {...register(item.fromKey)}
                                                            className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.fromKey] ? "border-red-500" : "border-gray-300"}`}
                                                        />
                                                        <input
                                                            type="datetime-local"
                                                            disabled={
                                                                !isAuthorizedToUpdate ||
                                                                !deadlineValue ||
                                                                (!isAuthorizedToUpdate && item.key === "commission_sales_rep" && !isSalesMemberAvailable) ||
                                                                (!isAuthorizedToUpdate && item.key === "commission_procurement_rep" && !isProcurementMemberAvailable)
                                                            }
                                                            min={watch(item.fromKey) || minDateTime}
                                                            max={deadlineValue || undefined}
                                                            {...register(item.toKey)}
                                                            className={`w-[175px] text-center border rounded-md py-1 text-sm ${errors[item.toKey] ? "border-red-500" : "border-gray-300"}`}
                                                        />
                                                        {isAuthorizedToUpdate &&
                                                            (watch(item.fromKey) || watch(item.toKey)) && (
                                                                <XCircleIcon
                                                                    className="h-5 w-5 text-red-500 cursor-pointer hover:text-red-700 transition-colors"
                                                                    onClick={() => {
                                                                        setValue(item.fromKey, "", { shouldValidate: true });
                                                                        setValue(item.toKey, "", { shouldValidate: true });
                                                                    }}
                                                                />
                                                            )}
                                                    </div>
                                                </div>
                                            </div>
                                            {errors[item.key] && (
                                                <p className="text-red-500 text-xs px-4">{t(errors[item.key].message.split('#')[0], { role: JSON.parse(errors[item.key].message.split('#')[1] || '{}')?.role })}</p>
                                            )}
                                            {(errors[item.fromKey] || errors[item.toKey]) && (
                                                <p className="text-red-500 text-xs px-4">
                                                    {t((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[0], { role: JSON.parse((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[1] || '{}')?.role, direction: JSON.parse((errors[item.fromKey] || errors[item.toKey])?.message.split('#')[1] || '{}')?.direction })}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium mb-1 capitalize">
                                    {t("note_label")} <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    rows="3"
                                    className="w-full border rounded-lg px-3 py-2"
                                    {...register("notes")}
                                    disabled={!isAuthorizedToUpdate}
                                    placeholder={t("note_placeholder")}
                                ></textarea>
                                {errors.notes && (
                                    <p className="text-red-500 text-sm">{t(errors.notes.message)}</p>
                                )}
                            </div>

                            {/* Deduction Reason */}
                            {isCommissionChanged && (
                                <div>
                                    <label className="block text-sm font-medium mb-1">
                                        {t("deduction_reason_label")} <span className="text-red-500">*</span>
                                    </label>

                                    <textarea
                                        rows="3"
                                        className="w-full border rounded-lg px-3 py-2"
                                        {...register("deduction_reason")}
                                        disabled={!isAuthorizedToUpdate}
                                        placeholder={t("deduction_reason_placeholder")}
                                    ></textarea>

                                    {errors.deduction_reason && (
                                        <p className="text-red-500 text-sm">
                                            {t(errors.deduction_reason.message)}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Important Commission Notice */}
                            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                                <p className="text-sm font-medium text-amber-900 capitalize">
                                    {t("important_notice_title")}
                                </p>

                                <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                                    {t("commission_deduction_notice")}
                                </p>
                            </div>
                        </div>
                    </DialogBody>
                </div>

                <DialogFooter className="flex justify-end gap-4 border-t pt-4">
                    <button type="button" onClick={onClose} className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100">
                        {ct("notNowButton")}
                    </button>
                    <button
                        type="submit"
                        disabled={!isAuthorizedToUpdate}
                        className={`px-5 py-2 rounded-lg font-medium shadow-sm transition-all ${isAuthorizedToUpdate
                            ? "bg-yellow-400 hover:bg-yellow-500 text-gray-900 cursor-pointer"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
                            }`}
                    >
                        {t("update_now_button")}
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default EditRFQCommissionDeadline;
